import React, { useState } from 'react';
import { Alert, Button, Form, Modal, Table } from 'react-bootstrap';
import { deleteRecordOverride, getRecords, putRecordOverride, recomputeRecords } from '../api/trainer';
import { RecordItem, RecordSource } from '../api/types';
import { errorMessage } from '../api/errors';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { SOURCE_LABELS, formatDate, formatDuration, formatPace, parseDuration } from '../utils/format';

const RecordsPage: React.FC = () => {
  const records = useAsync(() => getRecords(), []);
  const [editing, setEditing] = useState<RecordItem | null>(null);
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const openEditor = (item: RecordItem) => {
    setEditing(item);
    const current = item.override ?? item.effective;
    setTime(current?.timeS != null ? formatDuration(current.timeS) : '');
    setDate(item.override?.date ?? '');
    setNote(item.override?.note ?? '');
    setFormError(null);
  };

  const saveOverride = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    const seconds = parseDuration(time);
    if (seconds === null || seconds <= 0) {
      setFormError('Enter a time as m:ss or h:mm:ss.');
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await putRecordOverride(editing.distance, {
        timeS: seconds,
        date: date || null,
        note: note.trim() || null,
      });
      setEditing(null);
      setActionError(null);
      records.reload();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const revert = async (item: RecordItem) => {
    setActionError(null);
    setActionNotice(null);
    try {
      await deleteRecordOverride(item.distance);
      records.reload();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const recompute = async () => {
    setActionError(null);
    setActionNotice(null);
    try {
      const result = await recomputeRecords();
      setActionNotice(`Recomputed ${result.activities} ${result.activities === 1 ? 'activity' : 'activities'}.`);
      records.reload();
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };

  const rows = records.data?.items ?? [];

  return (
    <>
      <PageHeader title="Personal records">
        <p>
          Best times, computed from your activities. A manual time overrides the computed value until you revert it.
        </p>
        <div>
          <Button variant="outline-secondary" size="sm" onClick={recompute}>
            Recompute from activities
          </Button>
        </div>
      </PageHeader>

      {actionError && <Alert variant="danger">{actionError}</Alert>}
      {actionNotice && <Alert variant="success" className="py-2">{actionNotice}</Alert>}
      <StatusMessage loading={records.loading} error={records.error} onRetry={records.reload} />

      {records.data && (
        <section className="surface">
          <div className="table-responsive">
            <Table hover className="trainer-table">
              <thead>
                <tr>
                  <th>Distance</th>
                  <th className="text-end">Time</th>
                  <th>Pace</th>
                  <th>Source</th>
                  <th>Achieved</th>
                  <th className="text-end"><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(item => {
                  const effective = item.effective;
                  const source: RecordSource | null = effective?.source ?? null;
                  return (
                    <tr key={item.distance}>
                      <td>
                        {item.name}
                        {item.override && item.automatic && (
                          <div className="stat-hint">Computed {formatDuration(item.automatic.timeS)}</div>
                        )}
                      </td>
                      <td className="text-end tabular fw-semibold">{formatDuration(effective?.timeS)}</td>
                      <td className="tabular">
                        {effective?.timeS != null ? formatPace(effective.timeS / (item.meters / 1000)) : '—'}
                      </td>
                      <td>
                        {source ? <span className={`source-badge source-${source}`}>{SOURCE_LABELS[source]}</span> : '—'}
                      </td>
                      <td className="tabular">{effective?.date ? formatDate(effective.date) : '—'}</td>
                      <td className="text-end text-nowrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEditor(item)}
                          aria-label={`Set manual time for ${item.name}`}
                        >
                          {item.override ? 'Edit' : 'Override'}
                        </Button>
                        {item.override && (
                          <Button
                            variant="link"
                            size="sm"
                            className="ms-1"
                            onClick={() => revert(item)}
                            aria-label={`Revert ${item.name} to computed time`}
                          >
                            Revert
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </section>
      )}

      <Modal show={editing !== null} onHide={() => setEditing(null)} centered>
        <Form onSubmit={saveOverride}>
          <Modal.Header closeButton>
            <Modal.Title>Manual time · {editing?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="manual-time" className="mb-3">
              <Form.Label>Time (m:ss or h:mm:ss)</Form.Label>
              <Form.Control
                value={time}
                onChange={e => setTime(e.target.value)}
                inputMode="numeric"
                autoFocus
                isInvalid={formError !== null}
              />
              <Form.Text className="muted">
                Use this for a race result that the sync did not pick up. Revert returns to the computed record.
              </Form.Text>
            </Form.Group>
            <Form.Group controlId="manual-date" className="mb-3">
              <Form.Label>Date (optional, defaults to today)</Form.Label>
              <Form.Control type="date" value={date} onChange={e => setDate(e.target.value)} />
            </Form.Group>
            <Form.Group controlId="manual-note" className="mb-2">
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control value={note} onChange={e => setNote(e.target.value)} maxLength={255} />
            </Form.Group>
            {formError && <Alert variant="danger" className="py-2 mt-3 mb-0">{formError}</Alert>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              Save time
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default RecordsPage;
